import ArrowBackIosRoundedIcon from "@mui/icons-material/ArrowBackIosRounded";
import CircleNotificationsIcon from "@mui/icons-material/CircleNotifications";
import {
  Box,
  Button,
  Divider,
  Stack,
  Toolbar,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ref,
  onValue,
  query,
  limitToLast,
  orderByChild,
  endAt,
  update,
} from "firebase/database";
import { getDatabase } from "firebase/database";
import { firebaseApp, firebaseRDB } from "../../firebase";
import { useSelector } from "react-redux";
import moment from "moment";
import Cookies from "js-cookie";

const topics = [
  {
    name: "All",
    filter: "",
  },
  {
    name: "Kyc's",
    filter: "KYC",
  },
  {
    name: "Enquiries",
    filter: "ENQUIRY",
  },
  {
    name: "Trips",
    filter: "TRIP",
  },
  {
    name: "Payments",
    filter: "PAYMENT",
  },
];


const AdminNotificationPage = () => {
  const navigate = useNavigate();
  const adminId = Cookies.get("loggedInAdminId");

  const pageSize = 7;
  const [notificationList, setNotificationsList] = useState([]);
  const [lastKey, setLastKey] = useState("");
  const [hasMore, setHasMore] = useState(true);
  const [filter, setFilter] = useState("");
  const [unreadCounts, setUnreadCounts] = useState({});
  const [loadingUnread, setLoadingUnread] = useState(false);

  // Fetch unread counts for all categories
  const fetchUnreadCounts = async () => {
    if (!adminId) return;
    setLoadingUnread(true);
    const db = getDatabase(firebaseApp);
    const promises = topics.map(async (topic) => {
      let path = !topic.filter
        ? `history/generic/${adminId}`
        : `history/categorized/${adminId}/${topic.filter}`;
      const itemsRef = ref(db, path);
      return new Promise((resolve) => {
        onValue(
          itemsRef,
          (snapshot) => {
            const data = snapshot.val();
            let unread = 0;
            if (data) {
              unread = Object.values(data).filter((n) => !n.is_read).length;
            }
            resolve({ filter: topic.filter, count: unread });
          },
          { onlyOnce: true }
        );
      });
    });
    const results = await Promise.all(promises);
    const counts = {};
    results.forEach((r) => {
      counts[r.filter] = r.count;
    });
    setUnreadCounts(counts);
    setLoadingUnread(false);
  };

  // Mark all as read for current filter
  const markAllAsRead = async () => {
    if (!adminId) return;
    const db = getDatabase(firebaseApp);
    let path = !filter
      ? `history/generic/${adminId}`
      : `history/categorized/${adminId}/${filter}`;
    const itemsRef = ref(db, path);
    onValue(
      itemsRef,
      async (snapshot) => {
        const data = snapshot.val();
        if (data) {
          const updates = {};
          Object.entries(data).forEach(([key, value]) => {
            if (!value.is_read) {
              updates[key + "/is_read"] = true;
            }
          });
          if (Object.keys(updates).length > 0) {
            const updateRef = ref(db, path);
            await update(updateRef, updates);
            // Update local state only for current filter
            setNotificationsList((prev) =>
              prev.map((n) =>
                !n.is_read ? { ...n, is_read: true } : n
              )
            );
            fetchUnreadCounts();
          }
        }
      },
      { onlyOnce: true }
    );
  };

  const fetchNotifications = (filterValue, nextPage = false, lastKey = "") => {
    if (!adminId) return () => {};
    const db = getDatabase(firebaseApp);
    const getDbPath = () => {
      return !filterValue
        ? `history/generic/${adminId}`
        : `history/categorized/${adminId}/${filterValue}`;
    };

    let itemsRef = ref(db, getDbPath());
    let queryConstraints = [orderByChild("timestamp")];

    if (lastKey) {
      queryConstraints.push(endAt(lastKey));
    }

    queryConstraints.push(limitToLast(pageSize + 1)); // +1 to determine if there's a next page

    itemsRef = query(itemsRef, ...queryConstraints);

    const unsubscribe = onValue(itemsRef, (snapshot) => {
      const data = snapshot.val();
      let fetchedItems = data ? Object.values(data) : [];
      fetchedItems.reverse();
      if (lastKey) {
        fetchedItems.shift(); // Remove the first item (duplicate from previous fetch)
      }

      const newItems = nextPage
        ? [...notificationList, ...fetchedItems]
        : fetchedItems;
      setNotificationsList(newItems);

      const hasMoreItems = fetchedItems.length > 0;
      if (hasMoreItems) setHasMore(hasMoreItems);
      else setHasMore(false);

      if (hasMoreItems) {
        // Set the lastKey to the last item's timestamp to use for the next query
        setLastKey(fetchedItems[fetchedItems.length - 1].timestamp);
      }
    });

    return () => unsubscribe();
  };

  const handleFilterChange = (clickedFilter) => {
    setFilter(clickedFilter); // Update the filter state
    setNotificationsList([]); // Clear existing notifications
    setLastKey(""); // Reset the lastKey for pagination
    setHasMore(true); // Reset hasMore in case it was set to false
    setTimeout(() => {
      fetchNotifications(clickedFilter, false, "");
    }, 300);
  };

  // Helper to get correct notification path
  const getDbPath = (key) => {
    return !filter
      ? `history/generic/${adminId}/${key}`
      : `history/categorized/${adminId}/${filter}/${key}`;
  };

  useEffect(() => {
    if (!adminId) return;
    const unsubscribe = fetchNotifications("", true, "");
    fetchUnreadCounts();
    return () => unsubscribe();
  }, [adminId]);

  useEffect(() => {
    fetchUnreadCounts();
  }, [filter]);

  return (
    <Box pb={3}>
      <Stack
        position="fixed"
        width="100%"
        sx={{ background: "#EAEAEA", zIndex: 1000 }}
        py={2}
      >
        <Stack
          width="100%"
          m="auto"
          direction="row"
          maxWidth="lg"
          justifyContent="space-between"
          alignItems="center"
        >
          <Button
            sx={{ color: "#333", fontWeight: 700 }}
            onClick={() => navigate(-1)}
          >
            <ArrowBackIosRoundedIcon />
          </Button>

          <Typography
            fontWeight={700}
            color="#333"
            textAlign="center"
            flexGrow={1}
            variant="h5"
          >
            Notification
          </Typography>
        </Stack>
      </Stack>
      <Toolbar />
      <Stack
        direction="row"
        gap={2}
        justifyContent="flex-end"
        alignItems="center"
        p="1rem 6rem"
        position="fixed"
        width="100%"
        sx={{ background: "white", zIndex: 500, boxShadow: "0 2px 12px rgba(0,0,0,0.06)" }}
      >
        {/* Mark all as read button for current filter - moved to left, modern style */}
        <Box sx={{ display: "flex", alignItems: "center", mr: 2 }}>
          <Button
            variant="contained"
            disableElevation
            startIcon={<CircleNotificationsIcon sx={{ fontSize: 18 }} />}
            sx={{
              textTransform: "none",
              background: unreadCounts[filter] > 0 ? "#574964" : "#E0E0E0",
              color: unreadCounts[filter] > 0 ? "#fff" : "#918899",
              fontWeight: 700,
              borderRadius: 999,
              boxShadow: unreadCounts[filter] > 0 ? "0 2px 8px rgba(87,73,100,0.12)" : "none",
              px: 1.2,
              py: 0.5,
              minWidth: 0,
              fontSize: 13,
              transition: "background 0.2s",
              cursor: unreadCounts[filter] > 0 ? "pointer" : "not-allowed",
              opacity: unreadCounts[filter] > 0 ? 1 : 0.7,
              mr: 2,
              '&:hover': {
                background: unreadCounts[filter] > 0 ? '#6a5a7a' : '#E0E0E0',
              },
            }}
            onClick={unreadCounts[filter] > 0 ? markAllAsRead : undefined}
            disabled={unreadCounts[filter] === 0}
          >
            Mark all as read
          </Button>
        </Box>
        {topics.map((topic) => (
          <Button
            key={topic.filter}
            sx={{
              textTransform: "none",
              color: topic.filter === filter ? "#574964" : "#918899",
              fontWeight: 700,
              borderRadius: 999,
              background: topic.filter === filter ? "#f3f0fa" : "transparent",
              boxShadow: topic.filter === filter ? "0 2px 8px rgba(87,73,100,0.10)" : "none",
              borderBottom: topic.filter === filter ? "2px solid #574964" : "none",
              position: "relative",
              px: 1.5,
              py: 0.7,
              minWidth: 0,
              fontSize: 13,
              transition: "all 0.2s",
              '&:hover': {
                background: '#f3f0fa',
                color: '#574964',
                boxShadow: "0 2px 8px rgba(87,73,100,0.10)",
              },
            }}
            onClick={() => handleFilterChange(topic.filter)}
          >
            {topic.name}
            <Box
              component="span"
              sx={{
                ml: 1,
                background: topic.filter === filter ? "#574964" : "#E0E0E0",
                color: topic.filter === filter ? "#fff" : "#918899",
                borderRadius: 999,
                fontSize: 12,
                px: 1,
                minWidth: 18,
                height: 18,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: 700,
                boxShadow: topic.filter === filter ? "0 2px 8px rgba(87,73,100,0.10)" : "none",
                opacity: loadingUnread ? 0.5 : 1,
                border: topic.filter === filter ? "2px solid #fff" : "none",
              }}
            >
              {typeof unreadCounts[topic.filter] === "number" ? unreadCounts[topic.filter] : 0}
            </Box>
          </Button>
        ))}
      </Stack>
  <Toolbar />
  <Box sx={{ height: 18 }} />
  <Box
    m="auto"
    maxWidth="lg"
    sx={{
      maxHeight: 'calc(100vh - 260px)',
      overflowY: 'auto',
      pr: 1,
      mb: 0,
      // Custom scrollbar styling
      '&::-webkit-scrollbar': {
        width: '8px',
        background: '#f3f0fa',
        borderRadius: 8,
      },
      '&::-webkit-scrollbar-thumb': {
        background: '#b7afbd',
        borderRadius: 8,
      },
      '&': {
        scrollbarColor: '#b7afbd #f3f0fa',
        scrollbarWidth: 'thin',
      },
    }}
  >
    {notificationList.length ? (
      <>
        {notificationList.map((each, idx) => (
          <Notification
            data={each}
            key={each.key || idx}
            currentFilter={filter}
            setNotificationsList={setNotificationsList}
          />
        ))}
        {hasMore && (
          <Stack direction="row" justifyContent="center" mt={2} mb={2}>
            <Button
              sx={{ textTransform: "none" }}
              onClick={() => fetchNotifications(filter, hasMore, lastKey)}
            >
              Load more...
            </Button>
          </Stack>
        )}
      </>
    ) : (
      <Typography color="#333" mt={20} fontSize={18} textAlign="center">
        You have no notifications at the moment !
      </Typography>
    )}
  </Box>
      {!hasMore && notificationList.length > 0 && (
        <Typography textAlign="center" fontSize={16} color="#ADADAD" mt={5}>
          You're all caught up !
        </Typography>
      )}
    </Box>
  );
};
export default AdminNotificationPage;

const Notification = ({ data, setNotificationsList, currentFilter }) => {
  const adminId = Cookies.get("loggedInAdminId");

  // Use the notification's own filter if present, otherwise fall back to the
  // page's currentFilter (this fixes marking items in categorized paths).
  const notifFilter = data?.filter || currentFilter || "";

  // Helper to get correct notification path (use notification's own filter)
  const getDbPath = (key) => {
    return !notifFilter
      ? `history/generic/${adminId}/${key}`
      : `history/categorized/${adminId}/${notifFilter}/${key}`;
  };

  const markNotificationAsRead = async (key) => {
    if (data.is_read || !key) return;
    // Instantly update UI
    setNotificationsList((prev) =>
      prev.map((notification) =>
        notification.key === key
          ? { ...notification, is_read: true }
          : notification
      )
    );
    try {
      const notificationRef = ref(getDatabase(firebaseApp), getDbPath(key));
      await update(notificationRef, { is_read: true });
    } catch (error) {
      console.error("Error marking notification as read", error);
    }
  };

  return (
    <Box
      sx={{
        background: data.is_read ? "#E0E0E0" : "#f3f0fa",
        borderRadius: 3,
        boxShadow: data.is_read
          ? "none"
          : "0 2px 8px rgba(87,73,100,0.10)",
        mb: 1,
        px: 2,
        py: 1.2,
        cursor: "pointer",
        borderLeft: data.is_read ? "2px solid #E0E0E0" : "2px solid #574964",
        transition: "box-shadow 0.2s, background 0.2s",
        '&:hover': {
          background: data.is_read ? "#E0E0E0" : "#f3f0fa",
          boxShadow: "0 2px 8px rgba(87,73,100,0.18)",
        },
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
      }}
        onClick={async () => {
          if (!data.is_read) {
            await markNotificationAsRead(data.key);
          }
        }}
    >
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.2, width: "70%" }}>
        <CircleNotificationsIcon
          sx={{
            color: !data.is_read ? "#574964" : "#918899",
            fontSize: 26,
            mt: 0.5,
            flexShrink: 0,
          }}
        />
        <Box>
          <Typography
            color={data.is_read ? "#918899" : "#574964"}
            fontWeight={700}
            fontSize={14}
            sx={{ mb: 0.2, letterSpacing: 0.1, fontFamily: 'Montserrat, Arial, sans-serif' }}
          >
            {data.title}
          </Typography>
          <Typography
            color={data.is_read ? "#918899" : "#574964"}
            fontSize={12}
            fontWeight={400}
            sx={{ mb: 0.2, letterSpacing: 0.05, fontFamily: 'Montserrat, Arial, sans-serif' }}
          >
            {data.body}
          </Typography>
        </Box>
      </Box>
      <Typography
        color={data.is_read ? "#918899" : "#574964"}
        fontSize={10}
        fontWeight={500}
        sx={{ ml: 2, minWidth: 90, textAlign: "right", fontFamily: 'Montserrat, Arial, sans-serif', letterSpacing: 0.05 }}
      >
        {moment(data.timestamp).format("Do MMM YYYY, hh:mm A")}
      </Typography>
    </Box>
  );
};
